import React, {useState, useEffect} from 'react';
import {ArrowPathIcon, ChevronDownIcon} from '@heroicons/react/24/solid';
import styles from './Table.module.css';
import Calendar from '../calendar/Calendar';

export interface DateRangeFilterProps {
    label: string;
    startDate: Date | null;
    endDate: Date | null;
    onDateChange: (start: Date | null, end: Date | null) => void;
    isOpen: boolean;
    onToggle: () => void;
}

//Table 컴포넌트의 날짜 필터 컴포넌트
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
                                                             label,
                                                             startDate,
                                                             endDate,
                                                             onDateChange,
                                                             isOpen,
                                                             onToggle
                                                         }) => {
    const [selectingStart, setSelectingStart] = useState(true);
    const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate);
    const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate);

    // props가 변경될 때 내부 상태 동기화
    useEffect(() => {
        setTempStartDate(startDate);
        setTempEndDate(endDate);
        // 초기화 시에는 시작일 선택 모드로 리셋
        if (!startDate && !endDate) {
            setSelectingStart(true);
        }
    }, [startDate, endDate]);

    //날짜 선택
    const handleDateSelect = (date: Date) => {
        date.setHours(0, 0, 0, 0);
        //시작날짜와 종료날짜를 선택
        if (selectingStart) {
            setTempStartDate(date);
            setSelectingStart(false);
        } else {
            if (date >= (tempStartDate || date)) {
                setTempEndDate(date);
                onDateChange(tempStartDate, date);
                onToggle();
                setSelectingStart(true);
            }
        }
    };

    //선택 날짜 초기화
    const handleReset = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDateChange(null, null);
        setTempStartDate(null);
        setTempEndDate(null);
        setSelectingStart(true);
        onToggle();
    };

    //날짜데이터 형식 변환, 시간 데이터 제외
    const formatDate = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    //필터 내 선택 날짜 표시
    const getDisplayText = () => {
        //선택 안했을 경우 '전체'로 표시
        if (!startDate && !endDate) return '전체';
        //시작날짜~종료날짜 형식
        if (!endDate) return `${formatDate(startDate)} ~`;
        return `${formatDate(startDate)}~${formatDate(endDate)}`;
    };

    const getCalendarDisplayDate = () => {
        if (selectingStart) {
            return tempStartDate || startDate || new Date();
        }
        return tempEndDate || endDate || new Date();
    };

    //Calendar 컴포넌트 내 선택 날짜 표시
    const getDateDisplayText = (date: Date | null) => {
        //선택 안했을 경우 '-'로 표시
        return date ? formatDate(date) : '-';
    };
    return (
        <div className={styles.filterGroup}>
            <span className={styles.filterText}>{label}:</span>
            {/*필터창 버튼*/}
            <button
                className={styles.filterButton}
                onClick={(e) => {
                    e.stopPropagation();
                    onToggle();
                }}
            >
                <span>{getDisplayText()}</span>
                <ChevronDownIcon
                    className={`w-4 h-4 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>
            {/*필터창*/}
            {isOpen && (
                <div className={`${styles.filterDropdown} ${styles.dateRangeDropdown}`}>
                    <div className={styles.dateRangeHeader}>
                        <span>
                            {selectingStart ? '시작일 선택' : '종료일 선택'}
                        </span>
                        <button
                            onClick={handleReset}
                            className={styles.resetButton}
                            title="초기화"
                        >
                            <ArrowPathIcon className={styles.icon}/>
                        </button>
                    </div>
                    <div className={styles.dateRangeDisplay}>
                        <div>
                            {/*선택 시작일 표시*/}
                            <span>시작일: </span>
                            <span className={styles.dateText}>
                                {getDateDisplayText(tempStartDate)}
                            </span>
                        </div>
                        <div>
                            {/*선택 종료일 표시*/}
                            <span>종료일: </span>
                            <span className={styles.dateText}>
                                {getDateDisplayText(tempEndDate)}
                            </span>
                        </div>
                    </div>
                    {/*달력 컴포넌트 사용하여 날짜 선택*/}
                    <Calendar
                        selectedDate={getCalendarDisplayDate()}
                        onDateSelect={handleDateSelect}
                    />
                </div>
            )}
        </div>
    );
};

export default DateRangeFilter;